<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { EditorContent, useEditor } from '@tiptap/vue-3';
import { hasRichTextContent, richTextToPlainText, sanitizeRichTextHtml } from '~/lib/richText';
import { EditorColumn, EditorColumns } from './EditorColumns';
import Image from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';

const props = withDefaults(defineProps<{
    content?: string | null;
    emptyText?: string;
    variant?: 'default' | 'document';
}>(), {
    content: '',
    emptyText: '',
    variant: 'default',
});

const editor = useEditor({
    content: props.content || '',
    editable: false,
    immediatelyRender: false,
    extensions: [
        StarterKit.configure({ link: false, underline: false }),
        EditorColumn,
        EditorColumns,
        Image,
        TableKit.configure({
            table: {
                renderWrapper: true,
            },
        }),
        Underline,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Link.configure({
            openOnClick: true,
            protocols: ['http', 'https', 'mailto'],
            HTMLAttributes: {
                target: '_blank',
                rel: 'noopener noreferrer nofollow',
            },
        }),
    ],
});

const sanitizedDocumentHtml = computed(() => sanitizeRichTextHtml(props.content));

watch(() => props.content, (content) => {
    editor.value?.commands.setContent(content || '', { emitUpdate: false });
});

onBeforeUnmount(() => editor.value?.destroy());
</script>

<template>
    <div class="rich-text-content text-sm leading-6" :class="variant === 'document' ? 'rich-text-content--document text-foreground' : 'text-muted-foreground'">
        <div v-if="variant === 'document' && hasRichTextContent(content)" class="rich-text-document-body" v-html="sanitizedDocumentHtml" />
        <p v-else-if="variant === 'document' && emptyText">{{ emptyText }}</p>
        <ClientOnly v-else>
            <EditorContent v-if="editor && hasRichTextContent(content)" :editor="editor" />
            <p v-else-if="emptyText">{{ emptyText }}</p>
            <template #fallback>
                <p v-if="hasRichTextContent(content)">{{ richTextToPlainText(content) || 'Rich content' }}</p>
                <p v-else-if="emptyText">{{ emptyText }}</p>
            </template>
        </ClientOnly>
    </div>
</template>

<style scoped>
.rich-text-content :deep(.ProseMirror) {
    outline: none;
    width: 100%;
}
.rich-text-content--document .rich-text-document-body { width: 100%; font-size: var(--app-body-font-size); line-height: 1.7; }

.rich-text-content :deep(p),
.rich-text-content :deep(blockquote),
.rich-text-content :deep(ul),
.rich-text-content :deep(ol) {
    margin: 0 0 0.65rem;
}

.rich-text-content :deep(:last-child) {
    margin-bottom: 0;
}

.rich-text-content :deep(ul),
.rich-text-content :deep(ol) {
    padding-left: 1.25rem;
}

.rich-text-content :deep(a) {
    color: var(--primary);
    text-decoration: underline;
    text-underline-offset: 0.18em;
}
.rich-text-content--document .rich-text-document-body :deep(a) {
    overflow-wrap: anywhere;
    word-break: break-word;
}

.rich-text-content :deep(h1),
.rich-text-content :deep(h2),
.rich-text-content :deep(h3) {
    margin: 0.8rem 0 0.45rem;
    color: var(--foreground);
    font-weight: 600;
}
.rich-text-content :deep(hr) { margin: 1.25rem 0; border: 0; border-top: 1px solid var(--border); }
.rich-text-content :deep(pre) { margin: 0 0 0.75rem; overflow-x: auto; border-radius: var(--radius); background: var(--muted); padding: 0.75rem 1rem; font-family: var(--font-mono); font-size: 0.85rem; }
.rich-text-content :deep(img) { display: block; max-width: 100%; height: auto; margin: 0.75rem 0; border-radius: var(--radius); }
.rich-text-content :deep(.tableWrapper) { width: 100%; max-width: 100%; overflow-x: auto; }
.rich-text-content :deep(table) { width: 100%; min-width: 100%; border-collapse: collapse; table-layout: fixed; }
.rich-text-content :deep(caption) { caption-side: top; padding: 0.25rem 0; text-align: left; font-weight: 600; }
.rich-text-content :deep(th), .rich-text-content :deep(td) { border: 1px solid var(--border); padding: 0.45rem 0.6rem; vertical-align: top; }
.rich-text-content :deep(.ProseMirror > p),
.rich-text-content :deep(.ProseMirror > blockquote),
.rich-text-content :deep(.ProseMirror > ul),
.rich-text-content :deep(.ProseMirror > ol),
.rich-text-content :deep(.ProseMirror > h1),
.rich-text-content :deep(.ProseMirror > h2),
.rich-text-content :deep(.ProseMirror > h3),
.rich-text-content :deep(.ProseMirror > hr),
.rich-text-content :deep(.ProseMirror > pre) { max-width: 48rem; margin-left: auto; margin-right: auto; }
.rich-text-content--document .rich-text-document-body > p,
.rich-text-content--document .rich-text-document-body > blockquote,
.rich-text-content--document .rich-text-document-body > ul,
.rich-text-content--document .rich-text-document-body > ol,
.rich-text-content--document .rich-text-document-body > h1,
.rich-text-content--document .rich-text-document-body > h2,
.rich-text-content--document .rich-text-document-body > h3,
.rich-text-content--document .rich-text-document-body > hr,
.rich-text-content--document .rich-text-document-body > pre { max-width: 48rem; margin-left: auto; margin-right: auto; }
.rich-text-content :deep([data-editor-columns]) { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1.5rem; }
.rich-text-content :deep([data-editor-columns="3"]) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.rich-text-content--document { color: var(--foreground); }
.rich-text-content--document :deep(.ProseMirror) { font-size: var(--app-body-font-size); line-height: 1.7; }
.rich-text-content--document :deep(p) { margin-bottom: 0.9rem; }
.rich-text-content--document .rich-text-document-body :deep(p) { margin-bottom: 0.9rem; }
.rich-text-content--document :deep(h1) { margin: 1.35rem 0 0.9rem; color: var(--foreground); font-size: 1.9rem; font-weight: 700; line-height: 2.2rem; }
.rich-text-content--document :deep(h2) { margin: 1.2rem 0 0.85rem; color: var(--foreground); font-size: 1.35rem; font-weight: 600; line-height: 1.75rem; }
.rich-text-content--document :deep(h3) { margin: 1rem 0 0.75rem; color: var(--foreground); font-size: 1.12rem; font-weight: 600; line-height: 1.6rem; }
@media (max-width: 640px) {
    .rich-text-content :deep([data-editor-columns]), .rich-text-content :deep([data-editor-columns="3"]) { grid-template-columns: minmax(0, 1fr); }
}
</style>
